import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClipboardModule } from 'ngx-clipboard';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';

import 'hammerjs';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr'; // For core after login toast
import { CoreModule } from '@core/core.module';
import { CoreCommonModule } from '@core/common.module';
import { CoreSidebarModule, CoreThemeCustomizerModule } from '@core/components';

import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { FlexibleBenefitsComponent } from './main/pages/flexible-benefits/flexible-benefits.component';
import { FixedBenefitsComponent } from './main/pages/fixed-benefits/fixed-benefits.component';
import { BenefitsHistoryComponent } from './main/pages/benefits-history/benefits-history.component';
import { FlexibleBenefitsInfoComponent } from './main/pages/flexible-benefits/flexible-benefits-info/flexible-benefits-info.component';
import { BenefitSummaryComponent } from './main/pages/benefit-summary/benefit-summary.component';
import { CoreTouchspinModule } from '../@core/components/core-touchspin/core-touchspin.module';
import { BenefitDetailsComponent } from './main/pages/benefits-history/benefit-details/benefit-details.component';
import { SharedModule } from './main/shared/shared.module';
import { ProfileComponent } from './main/pages/profile/profile.component';
import { FixedBenefitsInfoComponent } from './main/pages/fixed-benefits/fixed-benefits-info/fixed-benefits-info.component';
import { HomeComponent } from './main/pages/dashboard/home.component';
import { ContentHeaderModule } from './layout/components/content-header/content-header.module';
import { Ng2FlatpickrModule } from 'ng2-flatpickr';
import { ChartsModule } from 'ng2-charts';
import { AuthGuard, ErrorInterceptor, JwtInterceptor } from './core/helpers';
import { Role } from './core/helpers/constants';
import { ReturnPaymentComponent } from './main/pages/return-payment/return-payment.component';
import { NoAuthGuards } from './core/helpers/no-auth.guards';
import { TermsOfServiceComponent } from './main/shared/terms-of-service/terms-of-service.component';
import { CookiePolicyComponent } from './main/shared/cookie-policy/cookie-policy.component';
import { PrivacyPolicyComponent } from './main/shared/privacy-policy/privacy-policy.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoginComponent } from './main/pages/authentication/login/login.component';
import { CompanyLoginComponent } from './main/pages/authentication/company-login/company-login.component';
import { FullRegisterComponent } from './main/pages/authentication/full-register/full-register.component';
import { RegisterComponent } from './main/pages/authentication/register/register.component';
import { ForgotPasswordComponent } from './main/pages/authentication/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './main/pages/authentication/reset-password/reset-password.component';
import { CustomRegisterComponent } from './main/pages/authentication/custom-register/custom-register.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoadingInterceptor } from './core/helpers/loading.interceptor';
import { BenefitDetailsService } from './core/service/benefit-details.service';
import { FlexibleSupplierInfoComponent } from './main/pages/flexible-benefits/flexible-supplier-info/flexible-supplier-info.component';
import { SubscriptionPaymentTableComponent } from './main/pages/benefit-summary/subscriptions-payment-table/subscriptions-payment-table.component';
import localeRo from '@angular/common/locales/ro';
import localeEn from '@angular/common/locales/en';
import { registerLocaleData } from '@angular/common';
import { ErrorHandlerService } from '../app/core/service/errorHandler.service';
import { BenChatComponent } from './shared/benChat/components/benchat/benchat.component';
import { QuillModule } from 'ngx-quill';
import { NotFoundComponent } from './main/pages/miscellaneous/not-found/not-found.component';
import { PagesModule } from './main/pages/pages.module';
import { NotAuthorizedComponent } from './main/pages/miscellaneous/not-authorized/not-authorized.component';
import { coreConfig } from './app-config';
import { NgSelectModule } from '@ng-select/ng-select';
import {SweetAlert2Module} from '@sweetalert2/ngx-sweetalert2';
import { LoginRedirectSSOComponent } from './main/pages/authentication/login-redirect-sso/login-redirect-sso.component';
import {CashbackShopListComponent} from './main/pages/cashback/cashback-shop-list/cashback-shop-list.component';
import {CashbackShopDetailsComponent} from './main/pages/cashback/cashback-shop-details/cashback-shop-details.component';
import {CashbackOfferDetailsComponent} from './main/pages/cashback/cashback-offer-details/cashback-offer-details.component';
import {SignupComponent} from './main/pages/authentication/signup/signup.component';
import {MobileCardPaymentComponent} from "./main/shared/mobile-card-payment/mobile-card-payment.component";
import { NoConnectivityComponent } from './main/pages/miscellaneous/no-connectivity/no-connectivity.component';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeRo, 'ro');


const appRoutes: Routes = [
  {
    path: '',
    component: CompanyLoginComponent,
    data: { animation: 'auth' },
    canActivate: [NoAuthGuards],
    pathMatch: 'full'
  },
  {
    path: 'auth/sso',
    component: LoginRedirectSSOComponent,
    canActivate: [NoAuthGuards],
    pathMatch: 'full'
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
    data: {
      animation: 'fadeIn'
    }
  },
  {
    path: ':subdomain',
    redirectTo: ':subdomain/home',
    pathMatch: 'full',
  },
  {
    path: ':subdomain',
    children: [
      {
        path: 'authentication/login',
        component: LoginComponent,
        data: { animation: 'auth' },
        canActivate: [NoAuthGuards],
      },
      {
        path: 'authentication/signup',
        component: SignupComponent,
        data: { animation: 'auth' },
        canActivate: [NoAuthGuards]
      },
      {
        path: 'authentication/register-full',
        component: FullRegisterComponent,
        data: { animation: 'auth' },
        canActivate: [NoAuthGuards],
      },
      {
        path: 'authentication/register/:registerToken',
        component: RegisterComponent,
        data: { animation: 'auth' },
        canActivate: [NoAuthGuards],
      },
      {
        path: 'authentication/forgot-password',
        component: ForgotPasswordComponent,
        data: { animation: 'auth' },
        canActivate: [NoAuthGuards],
      },
      {
        path: 'authentication/reset-password/:resetToken',
        component: ResetPasswordComponent,
        data: { animation: 'auth' },
        canActivate: [NoAuthGuards],
      },
      {
        path: 'home',
        component: HomeComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'home',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'flexible-benefits',
        component: FlexibleBenefitsComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'fixed-benefits',
        component: FixedBenefitsComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'fixed-benefits-info/:benefitId',
        component: FixedBenefitsInfoComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'flexible-benefits-info/:providerId/:benefitId',
        component: FlexibleBenefitsInfoComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'flexible-supplier-info/:providerId',
        component: FlexibleSupplierInfoComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'event-supplier-info/:eventId',
        component: FlexibleSupplierInfoComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'flexible-benefits-info/:providerId/benefit/:benefitId',
        component: FlexibleBenefitsInfoComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'benefit-summary/:id/:quantity/:meta',
        component: BenefitSummaryComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'benefits-history/:status',
        component: BenefitsHistoryComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'benefits-history',
        redirectTo: 'benefits-history/all',
        pathMatch: 'full',
      },
      {
        path: 'benefits-history/all',
        component: BenefitsHistoryComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'benefits-history/insurance',
        component: BenefitsHistoryComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'benefit-details/:historyType/:orderId',
        component: BenefitDetailsComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'payment-return/:orderId',
        component: ReturnPaymentComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'profile',
        component: ProfileComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn',
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'terms-of-service',
        component: TermsOfServiceComponent,
        data: {
          animation: 'fadeIn',
        },
      },
      {
        path: 'register-custom-fields/:companyId',
        component: CustomRegisterComponent,
        data: {
          animation: 'fadeIn',
        },
      },
      {
        path: 'deals',
        component: CashbackShopListComponent,
        data: {
          animation: 'fadeIn',
          roles: [Role.HR, Role.CLIENT],
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'deals/:shopId/:offerId',
        component: CashbackOfferDetailsComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn'
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'deals/:shopId',
        component: CashbackShopDetailsComponent,
        data: {
          roles: [Role.HR, Role.CLIENT],
          animation: 'fadeIn'
        },
        canActivate: [AuthGuard],
      },
      {
        path: 'cookie-policy',
        component: CookiePolicyComponent,
        data: {
          animation: 'fadeIn',
        },
      },
      {
        path: 'pages/miscellaneous/not-found',
        component: NotFoundComponent,
        data: {animation: 'misc'}
      },
      {
        path: 'pages/miscellaneous/not-authorized',
        component: NotAuthorizedComponent,
        data: {animation: 'misc'}
      },
      {
        path: 'mobile-card-payment',
        component: MobileCardPaymentComponent,
        canActivate: [NoAuthGuards]
      },
      {
        path: 'pages/miscellaneous/no-connectivity',
        component: NoConnectivityComponent,
        data: {animation: 'misc'}
      },
      {
        path: '**',
        redirectTo: 'pages/miscellaneous/not-found'
      },
    ]
  },
  {
    path: 'pages/miscellaneous/not-found',
    component: NotFoundComponent,
    data: {animation: 'misc'}
  },
  {
    path: 'https://mybenefits.ro/wp-content/uploads/2021/12/securitatea-informatiei-1.pdf',
    redirectTo: 'https://mybenefits.ro/wp-content/uploads/2021/12/securitatea-informatiei-1.pdf'
  },
  {
    path: 'https://mybenefits.ro/wp-content/uploads/2021/12/baza-date-actuala-temei-consimtamant.pdf',
    redirectTo: 'https://mybenefits.ro/wp-content/uploads/2021/12/baza-date-actuala-temei-consimtamant.pdf'
  },
];
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    BenefitSummaryComponent,
    BenChatComponent,
    HomeComponent,
    BenefitDetailsComponent,
    SubscriptionPaymentTableComponent,
    NoConnectivityComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ClipboardModule,
    QuillModule.forRoot(),
    HttpClientModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: 'enabled', // Add options right here
      relativeLinkResolution: 'legacy'
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    ContentHeaderModule,

    // NgBootstrap
    NgbModule,
    ToastrModule.forRoot(),
    Ng2FlatpickrModule,
    ChartsModule,

    // Forms
    NgSelectModule,
    FormsModule,

    // Core modules
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,

    // App modules
    LayoutModule,
    CoreTouchspinModule,
    SharedModule.forRoot(),
    PagesModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SweetAlert2Module,
  ],
  providers: [
    TranslateService,
    BenefitDetailsService,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
